import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { color } from "@tokens/index";
import { useViewport } from "@primitives/index";
import { NextButton, PresenceStack, SceneHeader, WorkflowViewSwitcher } from "@compositions/index";
import type { DestinationQueue, WorkflowViewMode } from "@compositions/index";
import { buildWorkflowSearch, useWorkflow } from "./workflows";
import { usePrefetchWorkflowTimeline } from "./workflows/timeline";
import { BoardBody } from "./workflows/board/BoardBody";
import { useBoardSelection } from "./workflows/board/useBoardSelection";
import { TimelineBody } from "./workflows/board/TimelineBody";
import type { QueueTreeResponse } from "./queues";
import { useWorkflowRealtime } from "./realtime";
import { useRecordPresence } from "./realtime/presence";
import styles from "./WorkflowScene.module.css";

export interface WorkflowSceneProps {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly currentUserId: string | undefined;
  /** Current queue context; becomes a spawned thread's originating queue. */
  readonly originatingQueueId: string | undefined;
  readonly queueTree: QueueTreeResponse | undefined;
  /** Operational queues returned by /queues/mine for the current user. */
  readonly memberQueueIds: ReadonlySet<string>;
  /** Queues the caller may file a task onto from the rail composer. */
  readonly destinationQueues?: readonly DestinationQueue[];
  /** Leave the scene (back to the workspace). */
  readonly onBack: () => void;
  /** Navigate internally to Einstein's entity workflows page. */
  readonly onOpenEntity?: () => void;
  /** Navigate to Galileo's page for this workflow's entity. */
  readonly onNavigateToGalileoEntity?: () => void;
  /** Detail view. The parent mirrors it into the `view` URL param. */
  readonly view: WorkflowViewMode;
  readonly onViewChange: (next: WorkflowViewMode) => void;
  /** Queue triage. Rendered on the trailing edge of the scene header. */
  readonly onNext?: () => void;
  readonly nextPending?: boolean;
}

/** Both bodies keep `.view` (the grid cell); hidden adds the fade-out modifier. */
function viewClass(active: boolean): string {
  return active ? `${styles.view}` : `${styles.view} ${styles.viewHidden}`;
}

/**
 * The workflow scene: one stable header and the view body under it. The
 * header renders once and never remounts on a Board/Timeline switch; the
 * bodies crossfade in one grid cell and stay mounted after their first
 * visit, so a switch back is instant and keeps drafts and scroll.
 *
 * Scene-level concerns live here: the workflow-level realtime subscription,
 * the workflow presence roster, the parent-workflow chip, and the compact
 * lane flag the header needs to hide its back control.
 */
export function WorkflowScene({
  id,
  entityType,
  entityId,
  currentUserId,
  originatingQueueId,
  queueTree,
  memberQueueIds,
  destinationQueues = [],
  onBack,
  onOpenEntity,
  onNavigateToGalileoEntity,
  view,
  onViewChange,
  onNext,
  nextPending = false,
}: WorkflowSceneProps): ReactElement {
  const navigate = useNavigate();
  const { isCompact, isAtLeast } = useViewport();

  useWorkflowRealtime({ workflowId: id, entityType, entityId });
  const { data: workflow } = useWorkflow(id, entityType, entityId);

  // Who else is here on this workflow. Also counts this user as present.
  const workflowViewers = useRecordPresence("workflow", id);
  const presence =
    workflowViewers.length > 0 ? (
      <PresenceStack
        members={workflowViewers}
        recordLabel="this workflow"
        filterSelfWhenAlone={true}
      />
    ) : null;

  // Parent chip data, fetched only for child workflows. The empty id keeps
  // the hook disabled for roots.
  const parentWorkflowId = workflow?.parentWorkflowId ?? "";
  const { data: parentWorkflow } = useWorkflow(parentWorkflowId, entityType, entityId);
  const handleNavigateToParent = useCallback(() => {
    if (parentWorkflow === undefined || workflow === undefined) return;
    // The spawning work item id rides in the parent URL so its board
    // pre-selects that task on arrival.
    const selection =
      workflow.parentWorkItemId !== null ? { itemId: workflow.parentWorkItemId } : undefined;
    const search = buildWorkflowSearch(
      parentWorkflow.entityType,
      parentWorkflow.entityId,
      originatingQueueId,
      selection,
    );
    void navigate(`/workflows/${parentWorkflow.id}?${search}`);
  }, [navigate, parentWorkflow, workflow, originatingQueueId]);

  // Compact: the board panel shows its own back control, so the header
  // hides its chevron while the panel is open. `laneOpen` covers the lane
  // rows (chat, notes) whose selection value is `null`.
  const { selectedItemId } = useBoardSelection();
  const [laneOpen, setLaneOpen] = useState(false);
  useEffect(() => {
    if (selectedItemId !== null) setLaneOpen(false);
  }, [selectedItemId]);
  useEffect(() => {
    setLaneOpen(false);
  }, [id]);
  const panelOpenOnCompact = isCompact && view === "board" && (selectedItemId !== null || laneOpen);

  // Bodies mount on first visit and stay mounted after. The timeline never
  // fetches until the user shows intent (hover/focus prefetch) or switches.
  const [visited, setVisited] = useState<ReadonlySet<WorkflowViewMode>>(() => new Set([view]));
  useEffect(() => {
    setVisited((prev) => (prev.has(view) ? prev : new Set([...prev, view])));
  }, [view]);

  const prefetchTimeline = usePrefetchWorkflowTimeline({ workflowId: id, entityType, entityId });
  const handleViewIntent = useCallback(
    (next: WorkflowViewMode) => {
      if (next === "timeline") prefetchTimeline();
    },
    [prefetchTimeline],
  );

  const headerWorkflow = useMemo(
    () =>
      workflow ?? {
        title: "",
        kind: "",
        entityType,
        entityId,
        entityDisplayName: "",
        parentWorkflowId: null,
      },
    [workflow, entityType, entityId],
  );

  return (
    <div
      data-testid="workflow-scene"
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: color.bg.canvas,
      }}
    >
      <SceneHeader
        workflow={headerWorkflow}
        onBack={panelOpenOnCompact ? undefined : onBack}
        parentTitle={parentWorkflow?.title}
        onNavigateToParent={parentWorkflow !== undefined ? handleNavigateToParent : undefined}
        onOpenEntity={onOpenEntity}
        onNavigateToGalileoEntity={onNavigateToGalileoEntity}
        presence={presence}
        viewSwitcher={
          <WorkflowViewSwitcher
            value={view}
            onChange={onViewChange}
            onIntent={handleViewIntent}
            compact={!isAtLeast("expanded")}
          />
        }
        {...(onNext !== undefined
          ? {
              next: <NextButton onNext={onNext} pending={nextPending} labeled={!isCompact} />,
            }
          : {})}
        compact={isCompact}
      />

      <div className={styles.stack}>
        {visited.has("board") && (
          <div
            data-testid="scene-view-board"
            className={viewClass(view === "board")}
            aria-hidden={view === "board" ? undefined : true}
          >
            <BoardBody
              id={id}
              entityType={entityType}
              entityId={entityId}
              currentUserId={currentUserId}
              originatingQueueId={originatingQueueId}
              queueTree={queueTree}
              memberQueueIds={memberQueueIds}
              destinationQueues={destinationQueues}
              laneOpen={laneOpen}
              onLaneOpenChange={setLaneOpen}
            />
          </div>
        )}
        {visited.has("timeline") && (
          <div
            data-testid="scene-view-timeline"
            className={viewClass(view === "timeline")}
            aria-hidden={view === "timeline" ? undefined : true}
          >
            <TimelineBody id={id} entityType={entityType} entityId={entityId} />
          </div>
        )}
      </div>
    </div>
  );
}
